import { ButtonUiState } from '@/components/Common/Button/ButtonUi';
import { NavigationTabItem } from '@/components/Common/NavigationTabs/UiModel';
import { OrderDeliveryInfoResponse } from '@/data/api/model/orders/detailOrdersDeliveryInfoDto';
import {
  OrderDetailsResponse,
  OrderItemResponse,
} from '@/data/api/model/orders/orderDetailsDto';
import { getStatusBadge } from '@/hooks/order/useGetDetailOrders/mapper';
import { formatDollarAmount } from '@/utils/amounts';
import { formatDayJsDate } from '@/utils/dateTime';
import { DashboardUserType } from '@/utils/enums';
import { firstOrNull } from '@/utils/functions';
import { Session } from '@/utils/session/model';
import dayjs from 'dayjs';
import { HeaderUi } from './Header/HeaderUi';
import { DeliveryDetailUi } from './OrderDeliveryDetail/OrderDeliveryDetailUI';
import { DetailActionsUi } from './OrderDetailActions/DetailActionsUi';
import { OrderDetailUi } from './OrderDetailUi';
import { OrderResumeUi } from './OrderResume/OrderResumeSection/OrderResumeUi';
import {
  OrderDetailProductUi,
  ProductListUi,
} from './ProductAndConfigurations/ProductList/ProductListUi';

export function orderDetailResponseToHeaderUi(
  input: OrderDetailsResponse
): HeaderUi {
  return {
    orderNumber: input.order_detail_number,
  };
}

export function orderDetailsResponseToOrderResumeUi(
  input: OrderDetailsResponse
): OrderResumeUi {
  const orderDate = dayjs(input.created_at);
  const ret: OrderResumeUi = {
    sellerName: input.seller_name,
    orderDate: orderDate.isValid() ? formatDayJsDate(orderDate) : '--',
    client: input.buyer_name,
    status: getStatusBadge(input.status),
    deliversToAddress: `${input.address.street}, ${input.address.colony}, ${input.address.state}, ${input.address.zip_code}`,
    paymentMethod: input.payment_method,
    paymentIcon: input.payment_icon,
    subTotalProductCount: `${input.order_items.length}`,
    subTotalAmount: `${formatDollarAmount(input.total_amount)}`,
    total: `${formatDollarAmount(input.total_amount)}`,
  };
  return ret;
}

function getNavTabs(
  orderDetailId: string,
  sessionData?: Session
): NavigationTabItem[] {
  if (sessionData?.type !== DashboardUserType.ADMIN) return [];
  const adminBasePath = `/admin/orders/detail/${orderDetailId}`;
  return [
    { to: adminBasePath, label: 'Productos y Configuraciones' },
    { to: `${adminBasePath}/delivery-evidence`, label: 'Evidencia de Entrega' },
  ];
}

export function orderDetailsResponseToOrderDetailUi(
  input: OrderDetailsResponse,
  sessionData: Session
): OrderDetailUi {
  const navTabs = getNavTabs(input.order_detail_id, sessionData);
  const ret: OrderDetailUi = {
    navTabs,
  };
  return ret;
}

export function orderDetailsResponseToDetailActionsUi(
  input: OrderDetailsResponse,
  sessionData?: Session,
  deliveryData?: OrderDeliveryInfoResponse
): DetailActionsUi {
  const isAdmin = sessionData?.type === DashboardUserType.ADMIN;
  const isSeller = sessionData?.type === DashboardUserType.SELLER;

  let acceptOrderButton: ButtonUiState | undefined;
  const shouldShowAcceptOrderButton =
    (isAdmin || isSeller) && input.status === 'created';
  if (shouldShowAcceptOrderButton) {
    acceptOrderButton = { label: 'Aceptar Pedido', disabled: false };
  }

  let deliveredOrderButton: ButtonUiState | undefined;
  const shouldShowMarkAsDeliveredButton =
    isAdmin && input.status === 'accepted';
  if (shouldShowMarkAsDeliveredButton) {
    const disabled =
      !deliveryData?.final_delivery_date.trim() ||
      !deliveryData?.final_total_order_detail_amount;
    deliveredOrderButton = {
      label: 'Marcar como entregado',
      disabled: disabled,
    };
  }
  const ret: DetailActionsUi = {
    acceptOrderButton,
    deliveredOrderButton,
    showCancelOrder:
      isAdmin &&
      (input.status === 'accepted' ||
        input.status === 'created' ||
        input.status === 'pending'),
    showSendOrder:
      isAdmin && (input.status === 'created' || input.status === 'pending'),
    showViewHistory: isAdmin,
  };
  return ret;
}

export function orderDetailResponseToProductListUi(
  input: OrderDetailsResponse
): ProductListUi {
  return {
    products: input.order_items.map(orderItemResponseToOrderDetailProductUi),
  };
}

function orderItemResponseToOrderDetailProductUi(
  input: OrderItemResponse
): OrderDetailProductUi {
  return {
    id: input.order_item_id,
    image: firstOrNull(input.product_image_url) ?? '',
    name: input.product_name,
    presentation: input.presentation,
    pricing: `$${input.unit_price_amount}/${input.unit_name}`,
    quantity: `${input.quantity} ${input.selector_unit_name}`,
    totalAmount: `${formatDollarAmount(input.total_order_item_amount)}`,
  };
}

export function orderDetailsResponseToOrderDeliveryDetail(
  input: OrderDetailsResponse,
  sessionData: Session
): DeliveryDetailUi {
  const isAdmin = sessionData?.type === DashboardUserType.ADMIN;
  return {
    deliveryDate: input.delivery.delivery_date
      ? dayjs(input.delivery.delivery_date)
      : null,
    deliveryNotes: input.delivery.delivery_notes ?? '',
    isEditable:
      isAdmin &&
      (input.status === 'created' ||
        input.status === 'pending' ||
        input.status === 'accepted'),
    showPlaceholders: isAdmin,
    deliveryStartTime: input.delivery.delivery_time
      ? dayjs(input.delivery.delivery_time, 'HH:mm')
      : null,
    deliveryEndTime: input.delivery.delivery_time_end
      ? dayjs(input.delivery.delivery_time_end, 'HH:mm')
      : null,
    saveDisabled: true,
  };
}
